import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  BookOpen, ClipboardList, UserCircle2,
  Menu, X as CloseIcon, Users, MapPin, NotebookPen, Home, Bell,
  Megaphone, CalendarRange, ClipboardCheck, PenSquare,
  MessagesSquare, ShieldAlert, Radio,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import LogoMark from "./LogoMark";
import wordmarkUrl from "./adarshabani-wordmark.svg";
import NotificationsBell from "./NotificationsBell";
import ThemeToggle from "./ThemeToggle";
import { currentTeacher } from "../services/teacherApi";

interface Props {
  title: string;
  // when set, shows a back link in the title row. By default this also hides
  // the bottom nav (treated like a nested page). Set `keepBottomNav: true` on
  // top-level pages — e.g. CreatePage — that want a "Back to dashboard"
  // affordance without losing the bottom tab strip.
  // onBack, when set, fully replaces the default nav(to) on the back button —
  // letting a page intercept the tap (e.g. the Create wizard's save-or-discard
  // prompt) before the teacher actually leaves.
  back?: { to: string; label?: string; keepBottomNav?: boolean; onBack?: () => void };
  children: React.ReactNode;
  rightAction?: React.ReactNode;
  // ProfilePage itself sets this so the hamburger menu's Profile entry doesn't
  // navigate to the page already being viewed (still rendered, but disabled).
  hideProfileButton?: boolean;
  // Drop <main>'s top padding. Pages with a `sticky top-0` toolbar (e.g.
  // LibraryPage's tabs) need this: a sticky element pins to the scroll
  // container's PADDING box, so any pt-4 leaves a band above it where scrolled
  // content peeks through. With flushTop the toolbar pins flush under the
  // header — the page then owns its own top spacing.
  flushTop?: boolean;
}

// Mobile-first fixed-shell layout. The whole viewport is a flex column with
// overflow hidden; only <main> scrolls. This is the standard PWA pattern:
// - header is the first flex item, padding-top consumes iOS safe-area (notch).
// - main is flex-1 + overflow-y-auto — independent scroll region.
// - BottomNav is the last flex item (NOT position: fixed) with padding-bottom
//   for the home-indicator safe-area. Being a flex item, it can't overlap main.
//
// Pages keep their existing internal layout; if they want a sticky toolbar
// (like LibraryPage's Live/Upcoming/Past tabs) they wrap it in `sticky top-0`
// inside main and it sticks to the top of the scroll area.
export default function MobileShell({ title, back, children, rightAction, hideProfileButton, flushTop }: Props) {
  const nav = useNavigate();
  const loc = useLocation();
  const teacher = currentTeacher();
  const [menuOpen, setMenuOpen] = useState(false);
  // Where the global header surfaces (bell, hamburger) send "back" — the page
  // they were opened on, so section roots like Notifications return the teacher
  // to where they were instead of always dumping them on the dashboard.
  const here = `${loc.pathname}${loc.search}`;
  // The floating bottom nav shows on top-level tabs + any page that opts in via
  // keepBottomNav. When shown it overlaps main, which pads its bottom to suit.
  const showNav = !back || !!back.keepBottomNav;

  return (
    <div className="relative h-[100dvh] flex flex-col overflow-hidden bg-void text-slate-200">
      {/* Brand header — flex-none, owns the iOS top safe-area */}
      <header
        className="flex-none border-b border-white/5 bg-void/85 backdrop-blur-md z-50 relative"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 min-w-0">
            <LogoMark className="w-8 h-8 text-white shrink-0" />
            <div className="min-w-0">
              <div className="font-display text-sm tracking-tight uppercase text-white leading-none">KINETIX</div>
              <img src={wordmarkUrl} alt="by Adarshabani" className="h-1.5 mt-1 opacity-70" />
            </div>
          </Link>
          <div className="flex items-center gap-1">
            {rightAction}
            <NotificationsBell
              from={here}
              onNavigate={({ pathname, hash, state }) =>
                nav(`${pathname}${hash || ""}`, state ? { state } : undefined)
              }
            />
            {/* Hamburger menu — Profile + Student Participation live inside.
                Replaces the bare profile icon so the header has one consistent
                entry-point for account/settings surfaces. */}
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              title="Menu"
              aria-label="Open menu"
              className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition-colors">
              <Menu size={18} />
            </button>
          </div>
        </div>
        {/* Page title row */}
        <div className="px-4 pb-3 flex items-center justify-between gap-3">
          {back ? (
            <button onClick={() => (back.onBack ? back.onBack() : nav(back.to))} className="text-cobalt-400 text-sm font-mono flex items-center gap-1">
              {/* When the referrer resolved to the notifications panel (arrived
                  via a notification tap), the label should say so — not the
                  page's default "Submissions"/"Doubts". */}
              ← {back.to.startsWith("/notifications") ? "Notifications" : (back.label || "Back")}
            </button>
          ) : null}
          <h1 className="text-lg font-semibold text-white tracking-tight truncate">{title}</h1>
          {teacher && !back && (
            <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest hidden xs:inline">{teacher.name.split(" ")[0]}</span>
          )}
        </div>
      </header>

      {/* Main — the ONLY scrolling element on the page. The floating nav overlaps
          it, so when shown we pad the bottom enough for the last content to clear
          the pill (plus the iOS home-indicator). */}
      <main
        className={`flex-1 overflow-y-auto px-4 ${flushTop ? "" : "pt-4"}`}
        style={{ paddingBottom: showNav ? "calc(env(safe-area-inset-bottom) + 5.5rem)" : "1rem" }}
      >{children}</main>

      {showNav && <BottomNav />}

      <AnimatePresence>
        {menuOpen && (
          <SideMenu
            onClose={() => setMenuOpen(false)}
            teacherName={teacher?.name}
            disableProfile={!!hideProfileButton}
            from={here}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Off-canvas hamburger menu. Sits in the same z-stack as the action sheets
// already used in LibraryPage so it doesn't conflict with them. Backdrop
// dismisses; per-row taps navigate then close. Profile + Student
// Participation are top-level entries here so the header can stay focused on
// navigation/notifications without a profile icon of its own.
function SideMenu({
  onClose, teacherName, disableProfile, from,
}: { onClose: () => void; teacherName?: string; disableProfile: boolean; from: string }) {
  const nav = useNavigate();

  // Close on Escape — handy on tablet/desktop sized screens where the keyboard
  // is reachable, harmless on phones.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const go = (path: string) => { onClose(); nav(path, { state: { from } }); };

  // Announcements and the routine builder are leadership actions — show those
  // entries only to admin/HoD (both endpoints are gated server-side regardless).
  const role = currentTeacher()?.role;
  const isOffice = role === "admin" || role === "hod";

  const items: Array<{ icon: React.ReactNode; label: string; sub?: string; onClick: () => void; disabled?: boolean }> = [
    {
      icon: <UserCircle2 size={18}/>,
      label: "Profile & settings",
      sub: "Scope, contact, push notifications",
      onClick: () => go("/profile"),
      disabled: disableProfile,
    },
    {
      icon: <Bell size={18}/>,
      label: "Notifications",
      sub: "Full history · filter by type & date",
      onClick: () => go("/notifications"),
    },
    // Announcements to parents — admin + HoD only, so plain teachers do not see
    // the entry at all (the page and the API both refuse them anyway). This slot
    // used to be "Staff notices", now dismantled — see the route in App.tsx.
    ...(isOffice ? [{
      icon: <Megaphone size={18}/>,
      label: "Announcements",
      sub: "Send a notice to parents' phones",
      onClick: () => go("/announcements"),
    }] : []),
    {
      // Open to every teacher: the start form only offers the (class, subject)
      // pairs in their assignments, and the server enforces the same.
      icon: <Radio size={18}/>,
      label: "Live classes",
      sub: "Teach online · quiz the class live",
      onClick: () => go("/live"),
    },
    {
      // The register, not the marking screen — the dashboard's "Take roll"
      // covers that. What belongs in a menu is the thing somebody goes looking
      // for rather than does every morning.
      icon: <ClipboardCheck size={18}/>,
      label: "Attendance register",
      sub: "Print a day or a date range",
      onClick: () => go("/student-attendance/register"),
    },
    {
      // Open to everyone: whether a teacher has anything to enter is decided by
      // the paper assignments the office made, and the list is simply empty for
      // a teacher with none — which is a clearer answer than a hidden entry.
      icon: <PenSquare size={18}/>,
      label: "Exam marks",
      sub: "Enter and submit your assigned papers",
      onClick: () => go("/exam-marks"),
    },
    {
      // Open to every teacher: a parent picks the subject teacher of their
      // child's class, so any teacher may have a thread waiting. Admin/HoD
      // additionally see the whole school's, read-only.
      icon: <MessagesSquare size={18}/>,
      label: "Parent doubts",
      sub: "Questions parents asked you about their child",
      onClick: () => go("/parent-doubts"),
    },
    // The family grievance queue. Admin + HoD only — the API refuses everyone
    // else, and a menu entry that leads to a 403 is worse than no entry.
    ...(isOffice ? [{
      icon: <ShieldAlert size={18}/>,
      label: "Parent complaints",
      sub: "Grievances raised from the Parent app",
      onClick: () => go("/parent-complaints"),
    }] : []),
    {
      icon: <Users size={18}/>,
      label: "Student participation",
      sub: "Past tests + scores per student",
      onClick: () => go("/students"),
    },
    // Building the timetable is a leadership action, and the page redirects
    // anyone else home.
    ...(isOffice ? [{
      icon: <CalendarRange size={18}/>,
      label: "Class routine",
      sub: "Build the weekly subject + teacher timetable",
      onClick: () => go("/routine"),
    }] : []),
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex justify-end"
      onClick={onClose}>
      <motion.aside
        initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
        transition={{ type: "tween", duration: 0.22 }}
        onClick={(e) => e.stopPropagation()}
        className="w-[85vw] max-w-sm h-full bg-void border-l border-white/10 shadow-2xl flex flex-col"
        style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>
        <div className="px-5 py-4 flex items-center justify-between border-b border-white/5">
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-widest text-cobalt-400">Menu</div>
            {teacherName && (
              <div className="text-white text-sm font-medium mt-0.5 truncate">{teacherName}</div>
            )}
          </div>
          <button onClick={onClose} aria-label="Close menu"
            className="p-2 -mr-1 rounded-lg text-slate-400 hover:text-white hover:bg-white/5">
            <CloseIcon size={18}/>
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-3">
          {items.map((it, i) => (
            <button key={i} type="button" onClick={it.disabled ? undefined : it.onClick}
              disabled={it.disabled}
              className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-colors mb-1 ${
                it.disabled
                  ? "opacity-40 cursor-default"
                  : "hover:bg-white/[0.04] active:bg-white/[0.06]"
              }`}>
              <span className="text-cobalt-400 shrink-0">{it.icon}</span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm text-white">{it.label}</span>
                {it.sub && <span className="block text-[11px] text-slate-500 mt-0.5 truncate">{it.sub}</span>}
              </span>
            </button>
          ))}

          {/* Appearance sits at the foot of the menu — set once, rarely
              revisited, so it shouldn't compete with the navigation entries. */}
          <div className="mt-2 pt-1 border-t border-white/5">
            <ThemeToggle />
          </div>
        </nav>
      </motion.aside>
    </motion.div>
  );
}

function BottomNav() {
  const loc = useLocation();
  // The bottom nav surfaces the teacher's most-used DAILY touchpoints, not
  // single-purpose actions. Home ("what's happening today") + Library ("the
  // test I want to grade") + Daily Progress (log today's classes) + Attendance
  // (check-in / check-out). Test creation lives on Home's Quick Actions —
  // it's an occasional action, so it doesn't claim prime nav real estate.
  const items = [
    { to: "/",           icon: Home,        label: "Home" },
    { to: "/library",    icon: BookOpen,    label: "Library" },
    { to: "/progress",   icon: NotebookPen, label: "Progress" },
    { to: "/attendance", icon: MapPin,      label: "Attendance" },
  ];
  // Floating frosted-glass pill. It stays the LAST flex item in MobileShell's
  // column — so it still can't overlap main's scroll area or a page's own
  // bottom bar (e.g. the Create wizard) — but renders as a detached, rounded
  // pill with side margins. The active tab carries a soft violet spotlight + a
  // glowing underline that SLIDE between tabs via framer shared-layout
  // (layoutId), the same motion language as the Library tabs. The outer flex
  // wrapper owns the iOS home-indicator safe-area.
  const spring = { type: "spring" as const, stiffness: 400, damping: 32 };
  return (
    <div
      className="pointer-events-none absolute inset-x-0 bottom-0 z-20 px-3 pt-2"
      style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 0.75rem)" }}
    >
      <nav className="pointer-events-auto relative mx-auto max-w-md overflow-hidden rounded-full border border-white/10 bg-white/[0.05] backdrop-blur-2xl shadow-2xl shadow-black/60">
        {/* Glassy sheen along the top edge */}
        <div className="pointer-events-none absolute inset-0 rounded-full bg-gradient-to-b from-white/[0.10] via-transparent to-transparent" />
        <div className="relative grid grid-cols-4 px-1">
          {items.map(({ to, icon: Icon, label }) => {
            // Exact-match for "/" so the Home tab doesn't light up on every
            // page the way a `startsWith("/")` test would.
            const active = to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(to);
            return (
              <Link key={to} to={to} className="relative flex items-center justify-center py-2.5">
                {active && (
                  <>
                    {/* Spotlight glow behind the active tab. Centred with
                        inset+margin-auto (not translate) so framer's layout
                        animation owns `transform` without a Tailwind clash. */}
                    <motion.span
                      layoutId="nav-glow" transition={spring}
                      className="pointer-events-none absolute inset-0 m-auto h-14 w-20 rounded-full bg-emerald-500/30 blur-2xl"
                    />
                    {/* Glowing underline at the pill's bottom edge */}
                    <motion.span
                      layoutId="nav-underline" transition={spring}
                      className="pointer-events-none absolute inset-x-0 bottom-0 mx-auto h-[2px] w-12 rounded-full bg-gradient-to-r from-emerald-500/0 via-emerald-400 to-emerald-500/0 shadow-[0_0_10px_2px_rgba(52,211,153,0.55)]"
                    />
                  </>
                )}
                <span className={`relative z-10 flex flex-col items-center gap-1 transition-colors ${
                  active ? "text-white" : "text-slate-500 hover:text-slate-300"
                }`}>
                  <Icon size={20} className={active ? "drop-shadow-[0_0_6px_rgba(52,211,153,0.5)]" : ""} />
                  <span className="text-[10px] font-mono uppercase tracking-widest">{label}</span>
                </span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

// Re-export so callers don't have to import lucide separately for common icons.
export { ClipboardList };
